import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, BarChart3, Target, Layers, Sparkles, Database } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import useReveal from "../lib/useReveal";
import useSEO from "../lib/useSEO";
import { caseStudies } from "../lib/caseStudiesData";

const services = [
  {
    icon: <MapPin size={26} strokeWidth={1.2} />,
    title: "Geofencing Campaigns",
    desc: "Custom-built virtual perimeters around venues, competitors, neighborhoods and events. We capture mobile devices that enter, building precision audiences from real-world movement.",
    points: ["Custom polygon fences", "Up to 60 days of capture history", "Premium venue targeting"],
  },
  {
    icon: <Layers size={26} strokeWidth={1.2} />,
    title: "Programmatic Advertising",
    desc: "We place your brand across a 600,000+ premium app and website network, with real-time bid optimization to maximize engagement per dollar.",
    points: ["Display, native, OTT and CTV", "Brand-safe inventory only", "Real-time optimization"],
  },
  {
    icon: <Target size={26} strokeWidth={1.2} />,
    title: "Audience Retargeting",
    desc: "We follow captured audiences across the internet with your message — for days, weeks, or months — until they convert.",
    points: ["Sequential creative", "Frequency capping", "Conversion attribution"],
  },
  {
    icon: <BarChart3 size={26} strokeWidth={1.2} />,
    title: "Data & Analytics",
    desc: "Live dashboards with impressions, click-through, foot traffic lift and revenue attribution. You see exactly what every dollar produced.",
    points: ["Live performance dashboards", "Foot-traffic attribution", "Custom reporting"],
  },
  {
    icon: <Sparkles size={26} strokeWidth={1.2} />,
    title: "Creative Design",
    desc: "Ad creative engineered for stopping power. Our design team crafts display, social and video assets that reflect the elegance of your brand.",
    points: ["Static & motion creative", "On-brand visual systems", "Multi-format delivery"],
  },
  {
    icon: <Database size={26} strokeWidth={1.2} />,
    title: "Campaign Strategy",
    desc: "End-to-end campaign architecture: audience mapping, geofence selection, creative rotation, KPIs and reporting cadence — built around your business goals.",
    points: ["Quarterly strategy reviews", "Competitor analysis", "Goal-aligned KPIs"],
  },
];

export default function ServicesPage() {
  useReveal();
  useSEO({
    title: "Services — Geofencing, Programmatic & Audience Retargeting",
    description: "Six disciplines, one obsession: geofencing campaigns, programmatic advertising, audience retargeting, data & analytics, creative design and campaign strategy for premium brands.",
    path: "/services",
  });
  return (
    <SiteShell>
      <section className="pt-32 pb-20 border-b border-white/5" data-testid="services-hero">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-6">Our Services</div>
          <h1 className="h-display text-white text-[48px] sm:text-[72px] lg:text-[96px]">
            BUILT FOR<br />
            <span className="text-[#d4af37]">PRECISION.</span>
          </h1>
          <p className="mt-8 max-w-[560px] text-white/70 text-[16px]">
            Six disciplines, one obsession: placing your brand in front of the
            right human at the moment that matters most.
          </p>
        </div>
      </section>

      <section className="py-24" data-testid="services-grid">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#d4af37]/10">
            {services.map((s, i) => (
              <div
                key={s.title}
                className="bg-[#0b0b0b] hover:bg-[#111111] p-10 lg:p-12 transition-colors duration-500 reveal"
                data-testid={`service-card-${i}`}
              >
                <div className="text-[#d4af37] mb-6">{s.icon}</div>
                <div className="text-[12px] tracking-[0.25em] text-white/40 mb-2">
                  0{i + 1}
                </div>
                <h3 className="text-white text-[22px] tracking-[0.05em] uppercase font-medium mb-5">
                  {s.title}
                </h3>
                <p className="text-white/65 text-[14px] leading-relaxed mb-7">{s.desc}</p>
                <ul className="space-y-2.5 mb-8">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-[13px] text-white/70">
                      <span className="mt-2 w-1 h-1 bg-[#d4af37] inline-block" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0d0d0d] border-t border-[#d4af37]/10" data-testid="case-studies-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-6">Campaign Wins</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] lg:text-[64px] mb-4">
            RESULTS THAT<br /><span className="text-[#d4af37]">SPEAK.</span>
          </h2>
          <p className="text-white/50 text-[13px] tracking-wide mb-14 max-w-[520px]">
            Real campaigns, anonymized to protect client confidentiality.{" "}
            <Link to="/case-studies" className="text-[#d4af37] hover:text-[#f2c66d]" data-testid="services-casestudies-link">
              View full case studies →
            </Link>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#d4af37]/10">
            {caseStudies.map((c, i) => (
              <div
                key={c.industry}
                className="bg-[#0b0b0b] hover:bg-[#111111] p-10 lg:p-12 transition-colors duration-500 reveal"
                data-testid={`case-study-card-${i}`}
              >
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">{c.industry}</div>
                <div className="text-[12px] text-white/40 tracking-wider mb-8">{c.context}</div>
                <div className="h-display text-white text-[44px] leading-none mb-1">{c.headline}</div>
                <div className="text-[13px] text-white/60 mb-8">{c.headlineLabel}</div>
                <ul className="space-y-3">
                  {c.stats.map((s) => (
                    <li key={s.label} className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-3">
                      <span className="text-[12px] tracking-wider uppercase text-white/45">{s.label}</span>
                      <span className="text-[15px] text-[#d4af37] font-medium">{s.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#111111] border-t border-[#d4af37]/15" data-testid="services-cta">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <div className="kicker mb-6">Ready When You Are</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] lg:text-[68px]">
            LET'S BUILD YOUR<br />NEXT <span className="text-[#d4af37]">CAMPAIGN.</span>
          </h2>
          <Link to="/campaign-builder" className="btn-gold btn-label mt-10 inline-flex" data-testid="services-apply-cta">
            Start Your Campaign <ArrowRight size={16} strokeWidth={1.6} />
          </Link>
        </div>
      </section>
    </SiteShell>
  );
}
